// components/admin/PainelParcelamentos.js
// Aba dentro do FinanceiroAdmin.js — mostra receita realizada vs a receber
// e a lista de parcelamentos por evento.
//
// Uso em FinanceiroAdmin.js:
//   import PainelParcelamentos from "./PainelParcelamentos"
//   <PainelParcelamentos eventoId={eventoSelecionado} />

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaMoneyBillWave, FaHourglassHalf, FaCheckCircle,
  FaExclamationTriangle, FaFileDownload, FaWhatsapp
} from "react-icons/fa";

const API = "https://api.ticketdovaqueiro.com/api";

const STATUS_LABEL = {
  em_andamento: { label: "Em andamento", color: "#2563eb" },
  concluido:    { label: "Concluído",    color: "#16a34a" },
  inadimplente: { label: "Inadimplente", color: "#dc2626" },
  cancelado:    { label: "Cancelado",    color: "#6b7280" },
};

export default function PainelParcelamentos({ eventoId }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState("todos");

  useEffect(() => {
    if (!eventoId) return;
    buscarDados();
  }, [eventoId]);

  const buscarDados = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${API}/relatorio/financeiro/${eventoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDados(data);
    } catch (err) {
      console.error("Erro ao carregar relatório:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    const token = localStorage.getItem("token");
    window.open(
      `${API}/relatorio/parcelados/${eventoId}/exportar?token=${token}`,
      "_blank"
    );
  };

  if (!eventoId) return <p style={{ color: "var(--color-text-secondary)" }}>Selecione um evento para ver o relatório.</p>;
  if (loading) return <p>Carregando...</p>;
  if (!dados) return <p>Sem dados disponíveis.</p>;

  const { resumo, pagamentosUnicos, parcelamentos, detalheParcelados } = dados;

  const detalheFiltrado = filtroStatus === "todos"
    ? detalheParcelados
    : detalheParcelados.filter((d) => d.status === filtroStatus);

  return (
    <div className="painel-parcelamentos">

      {/* Cards de resumo */}
      <div className="grid-resumo" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        <CardResumo
          titulo="Receita realizada"
          valor={`R$ ${resumo.receitaRealizada.toFixed(2)}`}
          icone={<FaCheckCircle />}
          cor="#16a34a"
        />
        <CardResumo
          titulo="A receber (parcelas)"
          valor={`R$ ${resumo.receitaAReceber.toFixed(2)}`}
          icone={<FaHourglassHalf />}
          cor="#2563eb"
        />
        <CardResumo
          titulo="Receita total prevista"
          valor={`R$ ${resumo.receitaTotal.toFixed(2)}`}
          icone={<FaMoneyBillWave />}
          cor="#7c3aed"
        />
        <CardResumo
          titulo="Inadimplentes"
          valor={parcelamentos.inadimplentes}
          icone={<FaExclamationTriangle />}
          cor="#dc2626"
        />
      </div>

      {/* Resumo por tipo */}
      <div className="resumo-tipos" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
        <div className="card-tipo">
          <h4>Pagamentos únicos</h4>
          <p>{pagamentosUnicos.quantidade} vendas — R$ {pagamentosUnicos.receita.toFixed(2)}</p>
        </div>
        <div className="card-tipo">
          <h4>Boletos parcelados</h4>
          <p>
            {parcelamentos.total} inscrições ·
            {parcelamentos.concluidos} concluídos ·
            {parcelamentos.emAndamento} em andamento ·
            {parcelamentos.inadimplentes} inadimplentes
          </p>
          <p>Realizado: <strong>R$ {parcelamentos.receitaRealizada.toFixed(2)}</strong> · A receber: <strong>R$ {parcelamentos.receitaAReceber.toFixed(2)}</strong></p>
        </div>
      </div>

      {/* Tabela de parcelamentos */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3>Detalhe dos parcelamentos</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="todos">Todos</option>
            <option value="em_andamento">Em andamento</option>
            <option value="concluido">Concluídos</option>
            <option value="inadimplente">Inadimplentes</option>
          </select>
          <button onClick={exportarCSV} title="Exportar CSV">
            <FaFileDownload /> CSV
          </button>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="extrato-tabela">
          <thead>
            <tr>
              <th>Vaqueiro</th>
              <th>Categoria</th>
              <th>Parcelas</th>
              <th>Realizado</th>
              <th>A receber</th>
              <th>Próx. vencimento</th>
              <th>Status</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {detalheFiltrado.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 24 }}>Nenhum registro.</td></tr>
            )}
            {detalheFiltrado.map((d) => {
              const statusCfg = STATUS_LABEL[d.status] || {};
              const proxVenc = d.proximoPagamento
                ? new Date(d.proximoPagamento).toLocaleDateString("pt-BR")
                : "—";

              return (
                <tr key={d.pedidoParceladoId}>
                  <td>{d.vaqueiro}</td>
                  <td>{d.categoria}</td>
                  <td>
                    {d.parcelasPagas}/{d.numeroParcelas}
                    {d.vencidas > 0 && (
                      <span style={{ color: "#dc2626", marginLeft: 4 }}>
                        ({d.vencidas} vencida{d.vencidas > 1 ? "s" : ""})
                      </span>
                    )}
                  </td>
                  <td style={{ color: "#16a34a", fontWeight: 500 }}>R$ {d.realizadoNeste.toFixed(2)}</td>
                  <td style={{ color: "#2563eb" }}>R$ {d.aReceberNeste.toFixed(2)}</td>
                  <td>{proxVenc}</td>
                  <td>
                    <span style={{
                      background: statusCfg.color + "22",
                      color: statusCfg.color,
                      padding: "2px 8px",
                      borderRadius: 4,
                      fontSize: 12,
                    }}>
                      {statusCfg.label}
                    </span>
                  </td>
                  <td>
                    {d.whatsapp && (
                      <a
                        href={`https://wa.me/55${d.whatsapp.replace(/\D/g, "")}?text=Olá ${d.vaqueiro}, sua parcela vence em ${proxVenc}. Acesse o boleto pelo app.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Cobrar via WhatsApp"
                        style={{ color: "#16a34a" }}
                      >
                        <FaWhatsapp size={18} />
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CardResumo({ titulo, valor, icone, cor }) {
  return (
    <div style={{
      background: "var(--color-background-secondary)",
      borderRadius: 8,
      padding: "12px 16px",
    }}>
      <div style={{ color: cor, fontSize: 20, marginBottom: 4 }}>{icone}</div>
      <p style={{ fontSize: 12, color: "var(--color-text-secondary)", margin: 0 }}>{titulo}</p>
      <p style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>{valor}</p>
    </div>
  );
}